import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { emailRegexConstant, passwordRegexConstant, phoneRegexConstant } from '../../constants';
import { loginApi, sendOTP, verifyOTP } from '../../apis';
import { AlertType, APIResponse, FreeKeyObject, LoginResponse, User } from '../../utilities/types';
import { showSnackbar } from '../../store/slices/common.slice';
import { getApiErrorMessage } from '../../utilities/helper';
import { login } from '../../store/slices/auth.slice';
import CustomAlert from '../elements/custom-alert.element';

interface LoginFormProps {
  onSuccess: () => void;
  registerCallback?: () => void;
  loginWithOtpCallback?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, registerCallback, loginWithOtpCallback }) => {
  const formikRef = useRef<FormikProps<{ username: string; password: string; mobile_number: string; otp: string }> | null>(null);
  const dispatch = useDispatch();
  const theme = useTheme();

  const [loginType, setLoginType] = useState<string>('password');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showResendOtpLink, setShowResendOtpLink] = useState<boolean>(false);
  const [showOtpField, setShowOtpField] = useState<boolean>(false);

  // 👁️ state for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const doSendOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    setShowResendOtpLink(false);
    try {
      const response = await sendOTP('mobile', values.username);
      if (response.status) {
        toggleAlertMessage('OTP has been sent to you mobile number.', 'info');
        setShowOtpField(true);
        setTimeout(
          () => {
            setShowResendOtpLink(true);
          },
          1 * 60 * 1000
        );
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const doVerifyOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = (await verifyOTP('mobile', values.username, values.otp, false, true)) as APIResponse<FreeKeyObject>;;
      if (response.status) {
        const { user, token } = response.data;
        const userWithRequiredFields = {
          id: user.id,
          name: user.name,
          email: user.email,
          ...user,
        };
        dispatch(showSnackbar({ message: 'Logged in successfully', severity: 'success' }));
        dispatch(login({ user: { ...userWithRequiredFields }, token }));
        onSuccess();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const onsubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!formikRef.current) {
      return;
    }
    if (loginType === 'otp') {
      if (showOtpField) {
        doVerifyOTP();
      } else {
        doSendOTP();
      }
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = (await loginApi(
        values.username,
        values.password
      )) as APIResponse<LoginResponse>;
      if (response.status) {
        const { user, token } = response.data;
        const userWithRequiredFields = {
          id: user.id,
          name: user.name,
          email: user.email,
          ...user,
        };
        dispatch(showSnackbar({ message: 'Logged in successfully', severity: 'success' }));
        dispatch(login({ user: { ...userWithRequiredFields }, token }));
        onSuccess();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const validationSchema: { [key: string]: any } = {
    username: Yup.string()
      .required('Email or phone number is required')
      .test('is-email-or-phone', 'Must be a valid email or 10-digit phone number', (value) => {
        if (!value) return false;
        return loginType === 'password'
          ? (emailRegexConstant.test(value) || phoneRegexConstant.test(value))
          : phoneRegexConstant.test(value);
      })
  };

  if (loginType === 'password') {
    validationSchema.password = Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required')
      .matches(
        passwordRegexConstant,
        'Password must contain at least one letter, one number and one special character'
      );
  } else if (showOtpField) {
    validationSchema.otp = Yup.string().required('OTP is required');
  }

  const loginSchema = Yup.object().shape(validationSchema);

  return (
    <Box>
      <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
        Welcome Back
      </Typography>
      <Typography align="center" color="text.secondary" sx={{ mb: 2 }}>
        Sign in to your Scrap Auction account
      </Typography>

      {/* Formik Form */}
      <Formik
        innerRef={formikRef}
        initialValues={{
          username: '',
          password: '',
          mobile_number: '',
          otp: ''
        }}
        validationSchema={loginSchema}
        validateOnMount
        onSubmit={(values) => {
          console.log('Form Submitted:', values);
        }}
      >
        {({ errors, touched, isValid, dirty, handleChange }) => (
          <Form>
            <Field
              as={TextField}
              name="username"
              label={loginType === 'otp' ? 'Phone Number *' : 'Email or Phone *'}
              placeholder={loginType === 'otp' ? 'Enter your phone number' : 'Enter your email or phone number'}
              fullWidth
              margin="normal"
              onChange={handleChange}
              error={touched.username && Boolean(errors.username)}
              helperText={touched.username && errors.username}
            />
            {loginType === 'password' && (
              <Field
                as={TextField}
                name="password"
                label="Password *"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                fullWidth
                margin="normal"
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {loginType === 'otp' && showOtpField && (
              <>
                <Field
                  as={TextField}
                  name="otp"
                  label="OTP *"
                  fullWidth
                  margin="normal"
                  onChange={handleChange}
                  error={touched.otp && Boolean(errors.otp)}
                  helperText={touched.otp && errors.otp}
                  sx={ {mb: 0 }}
                />
                {showResendOtpLink && (
                  <Typography
                    sx={{
                      m: 0,
                      textAlign: 'left',
                      letterSpacing: '0.5px',
                      fontSize: '10px !important',
                    }}
                  >
                    {`Didn't receieved OTP? `}
                    <Button
                      onClick={doSendOTP}
                      variant="text"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: '10px',
                        letterSpacing: '0.5px',
                        textDecoration: 'underline',
                        verticalAlign: 'baseline',
                        p: 0,
                        minWidth: 'auto',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    >
                      Resend again
                    </Button>
                  </Typography>
                )}
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
              onClick={(e) => onsubmit(e)}
              disabled={!isValid || !dirty || isSubmitting}
            >
              {loginType === 'password' ? 'Sign In' : (showOtpField ? 'Verify OTP' : 'Send OTP')}
            </Button>

            {alertMessage.message !== '' && (
              <Box sx={{ my: 1 }}>
                <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
              </Box>
            )}
            {loginType === 'password'
              ? <Typography align="center" sx={{ mt: 3 }}>
                Forgot Password?{' '}
                <Button
                  onClick={() => {
                    setLoginType('otp');
                  }}
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  Login with OTP
                </Button>
              </Typography>
              : <Typography align="center" sx={{ mt: 3 }}>
                Remember Password?{' '}
                <Button
                  onClick={() => {
                    setLoginType('password');
                    setShowOtpField(false);
                  }}
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  Login with Password
                </Button>
              </Typography>
            }

            {/* Sign In Link */}
            <Typography align="center" sx={{ mt: 2 }}>
              {`Don't have an account?`}{' '}
              <Button
                onClick={registerCallback}
                variant="text"
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                Register
              </Button>
            </Typography>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default LoginForm;
